import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ordersAPI } from '../services/api';
import { HiOutlinePrinter, HiArrowLeft } from 'react-icons/hi';

const OrderDetail = () => {
  const { id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchOrder();
  }, [id]);

  const fetchOrder = async () => {
    try {
      const response = await ordersAPI.getById(id);
      setOrder(response.data.order);
    } catch (error) {
      console.error('Error fetching order:', error);
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      confirmed: 'bg-blue-100 text-blue-800',
      processing: 'bg-purple-100 text-purple-800',
      shipped: 'bg-indigo-100 text-indigo-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800',
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getStatusStep = (status) => {
    const steps = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];
    return steps.indexOf(status);
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h2 className="font-serif text-2xl mb-4">Order not found</h2>
          <Link to="/orders" className="text-luxury-gold hover:underline">
            Back to Orders
          </Link>
        </div>
      </div>
    );
  }

  const statusSteps = ['Pending', 'Confirmed', 'Processing', 'Shipped', 'Delivered'];
  const currentStep = getStatusStep(order.status);

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className="bg-primary-50 py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link
            to="/orders"
            className="inline-flex items-center gap-2 text-sm text-gray-500 hover:text-luxury-gold transition-colors mb-4"
          >
            <HiArrowLeft className="w-4 h-4" />
            Back to Orders
          </Link>
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <h1 className="font-serif text-3xl md:text-4xl">Order #{order.orderNumber}</h1>
              <p className="text-gray-500 mt-1">
                Placed on {new Date(order.createdAt).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit'
                })}
              </p>
            </div>
            <button
              onClick={() => window.print()}
              className="flex items-center gap-2 px-4 py-2 border border-gray-200 text-sm hover:bg-gray-50 transition-colors"
            >
              <HiOutlinePrinter className="w-5 h-5" />
              Print
            </button>
          </div>
        </div>
      </section>

      <section className="py-12">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Status Timeline */}
          {order.status !== 'cancelled' && (
            <div className="bg-white p-6 mb-6">
              <div className="flex justify-between items-center relative">
                <div className="absolute left-0 right-0 top-1/2 h-1 bg-gray-200 -translate-y-1/2" />
                <div 
                  className="absolute left-0 top-1/2 h-1 bg-green-500 -translate-y-1/2 transition-all duration-500"
                  style={{ width: `${(currentStep / (statusSteps.length - 1)) * 100}%` }}
                />
                {statusSteps.map((step, index) => (
                  <div key={step} className="relative z-10 flex flex-col items-center">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${
                      index <= currentStep 
                        ? 'bg-green-500 text-white' 
                        : 'bg-gray-200 text-gray-500'
                    }`}>
                      {index < currentStep ? '✓' : index + 1}
                    </div>
                    <span className={`mt-2 text-xs ${
                      index <= currentStep ? 'text-gray-900' : 'text-gray-400'
                    }`}>
                      {step}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {order.status === 'cancelled' && (
            <div className="bg-red-50 border border-red-200 p-4 mb-6 text-center">
              <p className="text-red-700">This order has been cancelled</p>
            </div>
          )}

          <div className="grid lg:grid-cols-3 gap-6">
            {/* Order Items */}
            <div className="lg:col-span-2 bg-white p-6">
              <h3 className="font-serif text-lg mb-4">Order Items</h3>
              <div className="space-y-4">
                {order.items.map((item, index) => {
                  const variant = item.product?.variants?.find(v => v.sku === item.variant);
                  return (
                    <div key={index} className="flex gap-4 pb-4 border-b border-gray-100 last:border-0 last:pb-0">
                      <Link to={`/product/${item.product?.slug}`} className="w-20 h-24 flex-shrink-0">
                        <img
                          src={item.product?.images?.[0] || '/placeholder.jpg'}
                          alt={item.product?.name}
                          className="w-full h-full object-cover"
                        />
                      </Link>
                      <div className="flex-1">
                        <Link 
                          to={`/product/${item.product?.slug}`}
                          className="font-medium hover:text-luxury-gold transition-colors"
                        >
                          {item.product?.name}
                        </Link>
                        <p className="text-sm text-gray-500">{variant?.name}</p>
                        <p className="text-sm text-gray-500">Qty: {item.quantity}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-medium">{(item.price * item.quantity).toFixed(2)} TND</p>
                        <p className="text-sm text-gray-500">{item.price.toFixed(2)} TND each</p>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Order Summary */}
              <div className="mt-6 pt-6 border-t border-gray-100 space-y-2">
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Subtotal</span>
                  <span>{order.subtotal.toFixed(2)} TND</span>
                </div>
                {order.discount > 0 && (
                  <div className="flex justify-between text-sm text-green-600">
                    <span>Discount</span>
                    <span>-{order.discount.toFixed(2)} TND</span>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-600">Shipping</span>
                  <span>{order.shippingCost === 0 ? 'Free' : `${order.shippingCost.toFixed(2)} TND`}</span>
                </div>
                <div className="flex justify-between pt-2 border-t border-gray-100">
                  <span className="font-serif text-lg">Total</span>
                  <span className="font-serif text-xl">{order.total.toFixed(2)} TND</span>
                </div>
              </div>
            </div>

            {/* Order Info Sidebar */}
            <div className="space-y-6">
              {/* Status */}
              <div className="bg-white p-6">
                <h3 className="font-serif text-lg mb-4">Order Status</h3>
                <div className="space-y-3">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Status</span>
                    <span className={`px-3 py-1 text-xs rounded-full capitalize ${getStatusColor(order.status)}`}>
                      {order.status}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-gray-600">Payment</span>
                    <span className={`px-3 py-1 text-xs rounded-full capitalize ${
                      order.paymentStatus === 'paid' 
                        ? 'bg-green-100 text-green-800' 
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {order.paymentStatus}
                    </span>
                  </div>
                </div>
              </div>

              {/* Shipping Address */}
              <div className="bg-white p-6">
                <h3 className="font-serif text-lg mb-4">Shipping Address</h3>
                <p className="text-gray-600">
                  {order.shippingAddress.firstName} {order.shippingAddress.lastName}<br />
                  {order.shippingAddress.address}<br />
                  {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}<br />
                  {order.shippingAddress.country}
                </p>
                <p className="text-gray-600 mt-2">
                  Phone: {order.shippingAddress.phone}
                </p>
              </div>

              {/* Payment Method */}
              <div className="bg-white p-6">
                <h3 className="font-serif text-lg mb-4">Payment Method</h3>
                <p className="text-gray-600 capitalize">
                  {order.paymentMethod === 'cod' ? 'Cash on Delivery' : order.paymentMethod}
                </p>
              </div>

              {/* Notes */}
              {order.notes && (
                <div className="bg-white p-6">
                  <h3 className="font-serif text-lg mb-4">Order Notes</h3>
                  <p className="text-gray-600">{order.notes}</p>
                </div>
              )}

              {/* Tracking */}
              {order.trackingNumber && (
                <div className="bg-white p-6">
                  <h3 className="font-serif text-lg mb-4">Tracking</h3>
                  <p className="text-gray-600">
                    Tracking Number: <span className="font-medium">{order.trackingNumber}</span>
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OrderDetail;
